import { useEffect, useState } from 'react';

import { api } from '@/services';
import { useOAuth } from '@/hooks';
import { getErrorDetails, isAccountConversion } from '@/utils';

import useConversionPolling from './useConversionPolling';

const useRetryConversion = ({
  type,
  source,
  target,
  onUnauthorized,
  retryConversionId,
}) => {
  const {
    error,
    setError,
    progress,
    conversion,
    isComplete,
    resetState,
    stopPolling,
    isConverting,
    startPolling,
    setConversion,
    setIsComplete,
    setIsConverting,
  } = useConversionPolling({ onUnauthorized });

  const [alreadyRetried, setAlreadyRetried] = useState(false);

  const {
    source: { [source]: sourceAuth },
    target: { [target]: targetAuth },
  } = useOAuth();

  const {
    reset: sourceReset,
    accessToken: sourceToken,
    accountId: sourceAccountId,
  } = sourceAuth || {};

  const {
    reset: targetReset,
    accessToken: targetToken,
    accountId: targetAccountId,
  } = targetAuth || {};

  const needsSourceAuth = isAccountConversion(type);

  const sourceAuthId = needsSourceAuth
    ? sourceToken || sourceAccountId
    : undefined;

  const targetAuthId = targetToken || targetAccountId;

  // --- helpers: error handling ----------------------------------------------

  const resetAuth = (context) => {
    const { accountId } = context;

    if (sourceAccountId && sourceAccountId === accountId) {
      sourceReset();
    } else if (targetAccountId && targetAccountId === accountId) {
      targetReset();
    } else {
      sourceReset && sourceReset();
      targetReset && targetReset();
    }
  };

  const handleInitialError = (err) => {
    setConversion(null);
    setIsComplete(false);

    const { code, is401, is403, is500, message, context } =
      getErrorDetails(err);
    const status = err?.response?.status;

    if (is401) {
      onUnauthorized();
    } else if (status === 409 && code === 'ALREADY_RETRIED') {
      setAlreadyRetried(true);
    } else if (status === 409) {
      setError(
        'You have another conversion in progress. Please wait for it to finish.'
      );
    } else if (is403) {
      resetAuth(context);
    } else if (is500) {
      setError('Failed to retry. Please try again.');
    } else {
      setError(message);
    }

    setIsConverting(false);
  };

  // --- main action ----------------------------------------------------------

  const handleRetry = async ({ force } = {}) => {
    resetState();
    setAlreadyRetried(false);
    setIsConverting(true);

    try {
      const { id } = await api.retryConversion(retryConversionId, {
        force,
        sourceAuthId,
        targetAuthId,
      });

      startPolling(id);
    } catch (err) {
      handleInitialError(err);
    }
  };

  const forceRetry = () => handleRetry({ force: true });

  // --- lifecycle: cleanup on unmount ----------------------------------------

  useEffect(() => {
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- public API -----------------------------------------------------------

  return {
    error,
    progress,
    conversion,
    isComplete,
    forceRetry,
    handleRetry,
    isConverting,
    alreadyRetried,
  };
};

export default useRetryConversion;
